import { create } from 'zustand';
import { CreditRequestBatchLineItem } from '@/contexts/credit-request-context-types';

export type PaymentFlow =
  | { amountInMicros: number; status: 'full' }
  | {
      amountInMicros: number | null;
      status: 'partialAmount';
      subtotalInMicros: number;
    }
  | {
      amountInMicros: number | null;
      batchItems: CreditRequestBatchLineItem[];
      status: 'creditRequest';
      subtotalInMicros: number;
    };

export interface PaymentFlowStoreState {
  flow: PaymentFlow;
  reset: () => void;
  startCreditRequestFlow: (data: { subtotalInMicros: number }) => void;
  startFullPayFlow: (data: { amountInMicros: number }) => void;
  startPartialAmountFlow: (data: { subtotalInMicros: number }) => void;
  updateCreditRequestFlow: (data: {
    amountInMicros: number | null;
    batchItems: CreditRequestBatchLineItem[];
    subtotalInMicros: number;
  }) => void;
  updatePartialAmountFlow: (data: {
    amountInMicros: number | null;
    subtotalInMicros: number;
  }) => void;
}

const INITIAL_FLOW: PaymentFlow = { amountInMicros: 0, status: 'full' };

/**
 * State machine for the active payment session across screens.
 *
 * Survives orchestrator unmount (Android navigates into separate container
 * stacks during the credit-request flow), where local `useState` is lost. The
 * discriminated `PaymentFlow` union makes impossible states (e.g. a Full flow
 * with a partial amount) unrepresentable.
 *
 * @example
 * // Pay button: start the full pay flow
 * const store = usePaymentFlowStore.getState();
 * store.startFullPayFlow({ amountInMicros: subtotalInMicros });
 *
 * @example
 * // Partial pay: transition at button press, update at modal save
 * const store = usePaymentFlowStore.getState();
 * store.startPartialAmountFlow({ subtotalInMicros });
 * // ...later, at modal save:
 * store.updatePartialAmountFlow({ amountInMicros, subtotalInMicros });
 *
 * @example
 * // Short pay credit-request: transition at button press, update at workflow complete
 * const store = usePaymentFlowStore.getState();
 * store.startCreditRequestFlow({ subtotalInMicros });
 * // ...later, at workflow complete:
 * store.updateCreditRequestFlow({ amountInMicros, batchItems, subtotalInMicros });
 *
 * @example
 * // Read the effective amount from anywhere (closure-safe across unmount)
 * const store = usePaymentFlowStore.getState();
 * const amount = getEffectiveAmountInMicros(store.flow);
 */
export const usePaymentFlowStore = create<PaymentFlowStoreState>((set) => ({
  flow: INITIAL_FLOW,
  reset: () => set({ flow: INITIAL_FLOW }),
  startCreditRequestFlow: ({ subtotalInMicros }) =>
    set({
      flow: {
        amountInMicros: null,
        batchItems: [],
        status: 'creditRequest',
        subtotalInMicros,
      },
    }),
  startFullPayFlow: ({ amountInMicros }) =>
    set({ flow: { amountInMicros, status: 'full' } }),
  startPartialAmountFlow: ({ subtotalInMicros }) =>
    set({
      flow: { amountInMicros: null, status: 'partialAmount', subtotalInMicros },
    }),
  updateCreditRequestFlow: ({ amountInMicros, batchItems, subtotalInMicros }) =>
    set({
      flow: {
        amountInMicros,
        batchItems,
        status: 'creditRequest',
        subtotalInMicros,
      },
    }),
  updatePartialAmountFlow: ({ amountInMicros, subtotalInMicros }) =>
    set({
      flow: { amountInMicros, status: 'partialAmount', subtotalInMicros },
    }),
}));

export const getEffectiveAmountInMicros = (flow: PaymentFlow): number => {
  if (flow.status === 'full') {
    return flow.amountInMicros;
  }
  return flow.amountInMicros ?? flow.subtotalInMicros;
};

export const getBatchItems = (
  flow: PaymentFlow,
): CreditRequestBatchLineItem[] =>
  flow.status === 'creditRequest' ? flow.batchItems : [];

export const isPartialActive = (flow: PaymentFlow): boolean =>
  flow.status !== 'full';
